import * as yup from "yup";
import { array, boolean, date, number, object, string } from "yup";
import { RegulatoryTypeEnum } from "../../types";
import { dataKeySchema } from "./document-template";
import { BaseUserSchema } from "./user";

export const DocumentAnswerItemSchema = object({
  id: string().required(),
  type: string().required(),
  sequentialTypeNumber: number().required(),
  fields: object({}).required(),
  organizationId: string().required(),
  createdBy: string().required(),
  createdAt: date().required(),
  updatedAt: date().required(),
  archived: boolean().required(),
});

export interface DocumentAnswerItem
  extends yup.InferType<typeof DocumentAnswerItemSchema> {}

export const DocumentAnswerSchema = object({
  id: string().required(),
  answer: string().transform((value) => value ?? undefined),
  element: string<string>().defined().required(),
  documentVersionId: string().required(),
  createdBy: string().required(),
  createdAt: date().default(() => new Date()),
  updatedAt: date(),
  answerFileId: string()
    .uuid()
    .transform((value) => value ?? undefined),
  answerItems: array()
    .of(DocumentAnswerItemSchema)
    .transform((value) => value ?? undefined),
});

export interface DocumentAnswer
  extends yup.InferType<typeof DocumentAnswerSchema> {}

/**
 * A DocumentAnswer "baked in" / connected to a version at approval time (via the
 * backend `DocumentAnswerInDocumentVersion` join table). Used to resolve dynamic
 * data-key nodes against frozen values when previewing an approved version, so
 * the editor shows exactly what was connected at approval — see the two-tier
 * resolution rule in the dynamic-nodes resolver.
 */
export const ConnectedDocumentAnswerSchema = object({
  element: string().required(),
  documentAnswer: DocumentAnswerSchema.required(),
});

export interface ConnectedDocumentAnswer
  extends yup.InferType<typeof ConnectedDocumentAnswerSchema> {}

export const DocumentAnswerSuggestionSchema = object().shape({
  id: string().required(),
  suggestion: string().defined().strict(true),
  threadId: string().uuid().nullable(),
  element: string().required(),
  documentVersionId: string().required(),
  createdBy: string().required(),
  createdAt: date().default(() => new Date()),
  updatedAt: date(),
});

export interface DocumentAnswerSuggestion
  extends yup.InferType<typeof DocumentAnswerSuggestionSchema> {}

export enum DocumentVersionApproverStatus {
  USER_INVITE_SENT = "USER_INVITE_SENT",
  AWAITING_SIGNATURE = "AWAITING_SIGNATURE",
  APPROVED = "APPROVED",
}

export const DocumentVersionApproverSchema = object({
  id: string().uuid().required(),
  createdAt: date().required(),
  updatedAt: date().required(),
  documentVersionId: string().uuid().required(),
  userId: string().uuid(),
  approvalStatus: yup
    .mixed<DocumentVersionApproverStatus>()
    .oneOf(Object.values(DocumentVersionApproverStatus))
    .required(),
  user: BaseUserSchema.required(),
});

export interface DocumentVersionApprover
  extends yup.InferType<typeof DocumentVersionApproverSchema> {}

const SignatureRowSchema = object({
  process: string().required(),
  person: string().defined(),
  date: string().required(),
  time: string().defined(),
  initials: string().defined(),
});

const RevisionHistoryRowSchema = object({
  documentNumber: string().required(),
  revision: number().required(),
  date: string().required(),
  changeHistory: string().required(),
});

export const DocumentSignatureSchema = object({
  effectiveAt: date().nullable().defined(),
  signatureColumns: array()
    .of(
      object({
        key: string<keyof yup.InferType<typeof SignatureRowSchema>>()
          .oneOf(["process", "person", "date", "time", "initials"])
          .required(),
        label: string().required(),
      }).required()
    )
    .required(),
  signatureRows: array().of(SignatureRowSchema.required()).required(),
  revisionHistoryColumns: array()
    .of(
      object({
        key: string<keyof yup.InferType<typeof RevisionHistoryRowSchema>>()
          .oneOf(["documentNumber", "revision", "date", "changeHistory"])
          .required(),
        label: string().required(),
      }).required()
    )
    .required(),
  revisionHistoryRows: array()
    .of(RevisionHistoryRowSchema.required())
    .required(),
});

export interface DocumentSignature
  extends yup.InferType<typeof DocumentSignatureSchema> {}

export const BaseDocumentVersionSchema = object({
  id: string().required(),
  documentId: string().required(),
  readyForApproval: boolean().required(),
  documentVersionApprover: array()
    .of(DocumentVersionApproverSchema)
    .default([]),
  signatureTable: DocumentSignatureSchema.default(undefined).optional(),
  revision: number().nullable(),
  tiptapVersion: number().nullable(),
  readyForApprovalUpdatedAt: date().nullable().optional(),
  routedBy: string().uuid().nullable().optional(),
  // False for snapshots migrated from legacy revisions: their dynamic
  // contentTemplate was lost, so they must not be restored in Tiptap. The
  // backend always sends this (DB default true); optional here so callers only
  // need to handle the explicit `=== false` case.
  restorable: boolean().optional(),
  contentTemplate: string().nullable(),
  createdBy: string().required(),
  createdAt: date().default(() => new Date()),
  updatedAt: date(),
});

export const FileSchema = object({
  id: string().required(),
  filename: string().required(),
  mimeType: string().required(),
  organizationId: string().required(),
  createdAt: date().required(),
  createdBy: string().required(),
  path: string().required(),
  encoding: string().required(),
});

export interface File extends yup.InferType<typeof FileSchema> {}

export const UploadFileResponseSchema = FileSchema.shape({
  shareLink: object({
    token: string().required(),
  })
    .nullable()
    .optional(),
});

export interface UploadFileResponse
  extends yup.InferType<typeof UploadFileResponseSchema> {}

export const DataKeyInDocumentVersionSchema = object({
  documentVersionId: string().uuid().required(),
  dataKeyKey: string().required(),
  order: number().nullable(),
  dataKey: dataKeySchema.required(),
});

export interface DataKeyInDocumentVersion
  extends yup.InferType<typeof DataKeyInDocumentVersionSchema> {}

export const AnswerableDataKeySchema = object({
  dataKey: string().required(),
  required: boolean().required(),
  question: object().required(),
  context: array().of(string()).default([]),
});

export interface AnswerableDataKey
  extends yup.InferType<typeof AnswerableDataKeySchema> {}

export const DocumentVersionSchema = object({
  snapshot: string().nullable(),
  revisionSummary: string().default(""),
  answers: array().of(DocumentAnswerSchema).default([]),
  // Optional: only populated on version-fetch paths that include the bake join
  // table. Absent on the documents-list endpoint, so the resolver tolerates
  // `undefined` and falls back to live two-tier resolution.
  connectedAnswers: array().of(ConnectedDocumentAnswerSchema).optional(),
  suggestions: array().of(DocumentAnswerSuggestionSchema).default([]),
  fileId: string().uuid().nullable(),
  file: object({
    updatedAt: date(),
    encoding: string(),
    path: string(),
    createdBy: string(),
    createdAt: date(),
    organizationId: string(),
    mimeType: string(),
    filename: string(),
    id: string(),
  }).nullable(),
  changesFileId: string().uuid().nullable(),
  changesFile: object({
    updatedAt: date(),
    encoding: string(),
    path: string(),
    createdBy: string(),
    createdAt: date(),
    organizationId: string(),
    mimeType: string(),
    filename: string(),
    id: string(),
  }).nullable(),
  createdByUser: BaseUserSchema.required(),
  routedByUser: BaseUserSchema.nullable(),
  dataKeys: array().of(DataKeyInDocumentVersionSchema).default([]),
  answerableDataKeys: array().of(AnswerableDataKeySchema).default([]),
  contextBlockerTemplateKeys: array().of(string().required()).default([]),
}).concat(BaseDocumentVersionSchema);

export interface BaseDocumentVersion
  extends yup.InferType<typeof BaseDocumentVersionSchema> {}

export interface DocumentVersion
  extends yup.InferType<typeof DocumentVersionSchema> {}

export const SoftDeletionEventSchema = object({
  id: string().uuid().required(),
  createdAt: date().required(),
  purgeAfter: date().required(),
  createdBy: string().nullable().defined(),
  createdByUser: BaseUserSchema.nullable().defined(),
});

export interface SoftDeletionEvent
  extends yup.InferType<typeof SoftDeletionEventSchema> {}

export const BaseDocumentSchema = object({
  id: string().uuid().required(),
  templateKey: string<string>().nullable().defined(),
  deviceId: string().uuid().nullable(),
  folderId: string()
    .nullable()
    .transform((value) => value ?? null)
    .optional(),
  userUpload: boolean().required(),
  tiptapPath: string().nullable(),
  createdBy: string().required(),
  createdAt: date().required(),
  updatedAt: date().required(),
  softDeletionEvent: SoftDeletionEventSchema.nullable()
    .optional()
    .default(undefined),
  latestRevisionNumber: number().nullable().optional(),
  regulatoryType: string().oneOf(Object.values(RegulatoryTypeEnum)).nullable(),
  functionalGroupId: string().uuid().nullable(),
  title: string().required(),
});

export const DocumentSchema = object({
  versions: array().of(DocumentVersionSchema).default([]),
}).concat(BaseDocumentSchema);

export const DocumentReferenceSchema = BaseDocumentSchema.pick([
  "id",
  "title",
  "templateKey",
  "deviceId",
  "userUpload",
  "tiptapPath",
]).concat(
  object({
    versions: array()
      .of(
        object({
          createdAt: date().required(),
          file: object({ filename: string() }).nullable().defined(),
          answers: array()
            .of(object({ element: string().required() }))
            .required(),
        })
      )
      .required(),
  })
);

export interface DocumentReference
  extends yup.InferType<typeof DocumentReferenceSchema> {}

export const ReducedDocumentSchema = object({
  versions: array().of(BaseDocumentVersionSchema).default([]),
}).concat(BaseDocumentSchema);

export interface Document extends yup.InferType<typeof DocumentSchema> {}
export interface ReducedDocument
  extends yup.InferType<typeof ReducedDocumentSchema> {}

export const TrashedDocumentSchema = DocumentSchema.concat(
  object({
    softDeletionEvent: SoftDeletionEventSchema.required(),
    device: object({
      id: string().required(),
      name: string().required(),
    })
      .nullable()
      .defined(),
    folder: object({
      id: string().required(),
      name: string().required(),
      parentFolderId: string().nullable().defined(),
    })
      .nullable()
      .defined(),
    organization: object({
      id: string().required(),
      name: string().required(),
    }).required(),
  })
);

export interface TrashedDocument
  extends yup.InferType<typeof TrashedDocumentSchema> {}

// Schema for task assignee
const TaskAssigneeSchema = object({
  id: string().required(),
  email: string().required(),
  firstName: string().required(),
  lastName: string().required(),
});

// Schema for task device
const TaskDeviceSchema = object({
  id: string().required(),
  name: string().required(),
});

// Schema for unified task (manual tasks in the todos response)
export const UnifiedTaskSchema = object({
  id: string().required(),
  taskType: string()
    .oneOf(["MANUAL", "DOCUMENT", "APPROVAL", "QUALITY_EVENT", "TRAINING"])
    .required(),
  title: string().required(),
  description: string().nullable(),
  status: string().required(),
  assignee: TaskAssigneeSchema.nullable(),
  dueDate: string().nullable(),
  device: TaskDeviceSchema.nullable(),
  taskKey: string().nullable(),
  parentCardKey: string().nullable().optional(),
  documentId: string().nullable(),
  templateKey: string().nullable(),
  createdBy: string().nullable(),
  actionPath: string().nullable().optional(),
  qualityEventRecordId: string().nullable().optional(),
  qualityEventKey: string().nullable().optional(),
  qualityEventStepKey: string().nullable().optional(),
  // TRAINING-only fields (null for other task types)
  sourceOccurrenceId: string().nullable().optional(),
  sourceProgramId: string().nullable().optional(),
  sourceProgramName: string().nullable().optional(),
  documentVersionIdToTrainOn: string().nullable().optional(),
  trainingReadAt: string().nullable().optional(),
  trainingQuizId: string().nullable().optional(),
  trainingQuizPassedAt: string().nullable().optional(),
  trainingSignedAt: string().nullable().optional(),
  createdAt: string().required(),
  updatedAt: string().required(),
});

export interface UnifiedTaskType
  extends yup.InferType<typeof UnifiedTaskSchema> {}

export const UserTodosSchema = object({
  documentsTasks: object({
    toApprove: array().of(ReducedDocumentSchema).default([]),
    toAssignApprovers: array().of(ReducedDocumentSchema).default([]),
  }),
  manualTasks: array().of(UnifiedTaskSchema).default([]),
});

export interface UserTodos extends yup.InferType<typeof UserTodosSchema> {}

export const UserTodosSummarySchema = object({
  totalCount: number().required(),
  approvalsCount: number().required(),
  qualityEventCount: number().default(0),
  assignApproversCount: number().required(),
  manualCount: number().required(),
  trainingCount: number().default(0),
});

export interface UserTodosSummary
  extends yup.InferType<typeof UserTodosSummarySchema> {}

export const UserSurveyAnswersSchema = object({
  productStage: string().nullable(),
  productCertification: string().nullable(),
  productInterest: string().nullable(),
  productTour: string().nullable(),
  talkToExpert: string().nullable(),
  id: string().required(),
  userId: string().required(),
  createdAt: date().required(),
  updatedAt: date().required(),
});

export interface UserSurveyAnswers
  extends yup.InferType<typeof UserSurveyAnswersSchema> {}

export enum DocumentAnswerType {
  TEXT = "text",
  LIST = "list",
  FILE = "file",
}
